import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Camera, CheckCircle, Plus, Printer, Save, Trash2 } from 'lucide-react';
import Swal from 'sweetalert2';
import PageHeader from '../../../components/PageHeader';
import SelectorClienteProveedor from '../../../components/Tesoreria/SelectorClienteProveedor';
import ComprobanteScanModal from '../../../modules/cobranzas/components/ComprobanteScanModal.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import SucursalContexto, { labelSucursal } from '@/components/Tesoreria/SucursalContexto.jsx';
import { getSinaState } from '@/components/SinaAssistant/sinaStates.js';
import {
  abrirMinuta,
  fetchBancosActivos,
  fetchDocumentosCliente,
  fetchMinutaActiva,
  registrarCobranza,
} from '@/services/cobranzas.js';
import AccountStatementModal from '@/components/CuentaCorriente/AccountStatementModal.jsx';
import Combobox from '@/components/ui/Combobox.jsx';
import { bancosValoresApi } from '@/services/bancosValores.js';
import { readReceiptOperation, startNewReceiptOperation } from '@/utils/receipt-operation.js';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value || 0));

const MEDIOS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'TARJETA', label: 'Tarjeta' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'ECHEQ', label: 'eCheq' },
  { value: 'DEPOSITO', label: 'Deposito' },
  { value: 'MONI', label: 'Billetera virtual / Moni' },
  { value: 'OTRO', label: 'Otro' },
];

const MEDIOS_CON_REFERENCIA_OBLIGATORIA = new Set(['TRANSFERENCIA', 'TARJETA', 'CHEQUE', 'ECHEQ', 'DEPOSITO', 'MONI']);
const MEDIOS_CON_BANCO_O_CBU = new Set(['CHEQUE', 'ECHEQ']);

const DEFAULT_PAGO = {
  medio_pago: 'EFECTIVO',
  referencia: '',
  banco: '',
  banco_id: '',
  cbu: '',
  titular: '',
  cuit_titular: '',
  fecha_emision: '',
  fecha_cobro: '',
  moneda: 'ARS',
  cotizacion: '',
  importe: '',
};

const getReferenciaLabel = (medioPago) => {
  if (medioPago === 'TRANSFERENCIA') return 'Referencia / nro. operacion';
  if (medioPago === 'TARJETA') return 'Cupon / lote / autorizacion';
  if (medioPago === 'CHEQUE') return 'Nro. cheque';
  if (medioPago === 'ECHEQ') return 'Identificador eCheq *';
  if (medioPago === 'DEPOSITO') return 'Comprobante de deposito';
  if (medioPago === 'MONI') return 'Referencia billetera';
  return 'Referencia';
};

const getPendingContrareembolso = (guia) =>
  Number(guia?.saldo_contrareembolso_pendiente ?? guia?.contrareembolso ?? guia?.saldo ?? 0);
const sinaWarning = getSinaState('warning');

const toAmount = (value) => Math.max(0, Math.round(Number(value || 0) * 100) / 100);

export const normalizeApplications = (documentos, imputaciones, totalPagos, isContraReceiptMode) => {
  let available = toAmount(totalPagos);
  const next = { facturas: {}, ajustes: {}, guias: {} };
  const apply = (tipo, documento) => {
    const requested = toAmount(imputaciones?.[tipo]?.[documento.id]);
    const balance = toAmount(tipo === 'guias' && isContraReceiptMode
      ? getPendingContrareembolso(documento)
      : documento.saldo);
    const amount = Math.min(requested, balance, available);
    next[tipo][documento.id] = amount;
    available = toAmount(available - amount);
  };
  (documentos.facturas || []).forEach((documento) => apply('facturas', documento));
  (documentos.ajustes || []).forEach((documento) => apply('ajustes', documento));
  (documentos.guias || []).forEach((documento) => apply('guias', documento));
  return next;
};

export default function RegistrarCobranza({ receiptMode = 'general' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { sucursal, user, tenant, can = () => false } = useAuth();
  const modeFromState = String(location.state?.receiptMode || '').trim().toLowerCase();
  const isContraReceiptMode = receiptMode === 'contrareembolso' || modeFromState === 'contrareembolso';
  const prefillCliente = location.state?.prefillCliente || null;
  const prefillFactura = location.state?.prefillFactura || null;
  const prefillFacturaId = prefillFactura?.id ? Number(prefillFactura.id) : null;
  const prefillGuiaId = location.state?.prefillGuiaId ? Number(location.state.prefillGuiaId) : null;
  const prefillImporte = location.state?.prefillImporte ? Number(location.state.prefillImporte) : null;

  const [showSelectorCliente, setShowSelectorCliente] = useState(false);
  const [errores, setErrores] = useState({});
  const [comprobante, setComprobante] = useState(null);
  const [minutaState, setMinutaState] = useState({ loading: false, minuta: null, error: '' });
  const [cliente, setCliente] = useState(prefillCliente);
  const [documentos, setDocumentos] = useState({ facturas: [], guias: [], ajustes: [] });
  const [imputaciones, setImputaciones] = useState({ facturas: {}, guias: {}, ajustes: {} });
  const [pagos, setPagos] = useState([]);
  const [retenciones, setRetenciones] = useState([]);
  const [tiposRetencion, setTiposRetencion] = useState([]);
  const [retencionDraft, setRetencionDraft] = useState(null);
  const [observaciones, setObservaciones] = useState('');
  const [fechaCobranza, setFechaCobranza] = useState(new Date().toISOString().split('T')[0]);
  const [tab, setTab] = useState(isContraReceiptMode ? 'guias' : 'facturas');
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanPurpose, setScanPurpose] = useState('comprobante');
  const [scanPagoIndex, setScanPagoIndex] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [registrarSaldoACuenta, setRegistrarSaldoACuenta] = useState(false);
  const [rendirDirectamente, setRendirDirectamente] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const operationFlow = `registrar:${isContraReceiptMode ? 'CONTRAREEMBOLSO' : 'GENERAL'}`;
  const [savedOperation, setSavedOperation] = useState(null);
  const [operationError, setOperationError] = useState('');
  const refreshOperation = () => {
    try { setSavedOperation(readReceiptOperation(operationFlow)); setOperationError(''); }
    catch (error) { setSavedOperation(null); setOperationError(error.message); }
  };
  useEffect(() => {
    refreshOperation();
    window.addEventListener('storage', refreshOperation);
    return () => window.removeEventListener('storage', refreshOperation);
  }, [operationFlow, user?.id, tenant, sucursal?.id]);
  const [editingPagoIndex, setEditingPagoIndex] = useState(null);
  const [pagoDraft, setPagoDraft] = useState(null);
  const [guidedPayment, setGuidedPayment] = useState(false);
  const [bancosActivos, setBancosActivos] = useState([]);
  const paymentPanelRef = useRef(null);

  useEffect(() => {
    let active = true;
    bancosValoresApi.valueTypes({ activo: true })
      .then((response) => {
        if (active) setTiposRetencion((response?.items || []).filter((tipo) => (
          String(tipo.categoria || '').toUpperCase() === 'RETENCION'
          && tipo.activo !== false
          && tipo.activo !== 0
        )));
      })
      .catch(() => {
        if (active) setTiposRetencion([]);
      });
    return () => { active = false; };
  }, []);

  const totalPagos = useMemo(
    () => pagos.reduce((acc, pago) => acc + Number(pago.importe || 0), 0),
    [pagos],
  );

  const totalRetenciones = useMemo(
    () => retenciones.reduce((acc, retencion) => acc + Number(retencion.importe || 0), 0),
    [retenciones],
  );
  const totalDisponible = totalPagos + totalRetenciones;

  const totalSolicitado = useMemo(() => {
    const facturas = Object.values(imputaciones.facturas || {}).reduce((acc, val) => acc + Number(val || 0), 0);
    const guias = Object.values(imputaciones.guias || {}).reduce((acc, val) => acc + Number(val || 0), 0);
    const ajustes = Object.values(imputaciones.ajustes || {}).reduce((acc, val) => acc + Number(val || 0), 0);
    return facturas + guias + ajustes;
  }, [imputaciones]);

  const totalAplicado = totalSolicitado;
  const remanente = Math.max(totalDisponible - totalAplicado, 0);
  const saldoRestanteDocumentos = useMemo(() => {
    const saldoRestante = (tipo, lista) => lista.reduce((total, documento) => {
      const aplicado = toAmount(imputaciones?.[tipo]?.[documento.id]);
      const saldo = toAmount(tipo === 'guias' && isContraReceiptMode
        ? getPendingContrareembolso(documento)
        : documento.saldo);
      return total + (aplicado > 0 ? Math.max(saldo - aplicado, 0) : 0);
    }, 0);
    return saldoRestante('facturas', documentos.facturas || [])
      + saldoRestante('ajustes', documentos.ajustes || [])
      + saldoRestante('guias', documentos.guias || []);
  }, [documentos, imputaciones, isContraReceiptMode]);

  useEffect(() => {
    let active = true;
    fetchBancosActivos()
      .then((data) => { if (active) setBancosActivos(data?.items || []); })
      .catch(() => { if (active) setBancosActivos([]); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setImputaciones((prev) => {
      const next = normalizeApplications(documentos, prev, totalDisponible, isContraReceiptMode);
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, [documentos, isContraReceiptMode, totalDisponible]);

  useEffect(() => {
    // La sucursal es el límite operativo: nunca conservamos documentos o medios de otro contexto.
    setCliente(null);
    setDocumentos({ facturas: [], guias: [], ajustes: [] });
    setImputaciones({ facturas: {}, guias: {}, ajustes: {} });
    setPagos([]);
    setRetenciones([]);
    setRetencionDraft(null);
    setPagoDraft(null);
    setEditingPagoIndex(null);
    setGuidedPayment(false);
    setRegistrarSaldoACuenta(false);
    setRendirDirectamente(false);
    const cargarMinuta = async () => {
      setMinutaState({ loading: true, minuta: null, error: '' });
      try {
        const data = await fetchMinutaActiva({ sucursalId: sucursal?.id });
        setMinutaState({ loading: false, minuta: data?.minuta || null, error: '' });
      } catch (error) {
        if (error?.response?.status === 404) {
          setMinutaState({ loading: false, minuta: null, error: '' });
          const confirmation = await Swal.fire({
            imageUrl: sinaWarning.image,
            imageAlt: 'Asistente en estado de advertencia',
            imageWidth: 112,
            imageHeight: 112,
            title: 'No hay minuta abierta',
            text: 'Para registrar una cobranza en esta sucursal, necesito abrir una minuta. ¿Querés que la abra ahora?',
            showCancelButton: true,
            confirmButtonText: 'Sí, abrir minuta',
            cancelButtonText: 'Ahora no',
            confirmButtonColor: '#14b8a6',
          });
          if (confirmation.isConfirmed) {
            try {
              setMinutaState({ loading: true, minuta: null, error: '' });
              const data = await abrirMinuta({ sucursal_id: sucursal?.id });
              setMinutaState({ loading: false, minuta: data?.minuta || null, error: '' });
              await Swal.fire({ icon: 'success', title: 'Minuta abierta', text: 'Ya podés registrar la cobranza.' });
            } catch (openError) {
              setMinutaState({ loading: false, minuta: null, error: openError?.response?.data?.message || 'No se pudo abrir la minuta.' });
            }
          }
          return;
        }
        setMinutaState({
          loading: false,
          minuta: null,
          error: error?.response?.data?.message || 'No se pudo obtener la minuta activa.',
        });
      }
    };
    cargarMinuta();
  }, [sucursal?.id]);

  const handleAbrirMinuta = async () => {
    if (!sucursal?.id) return;
    const confirmation = await Swal.fire({
      imageUrl: sinaWarning.image,
      imageAlt: 'Asistente en estado de advertencia',
      imageWidth: 112,
      imageHeight: 112,
      title: 'No hay minuta abierta',
      text: 'Para registrar esta cobranza, SINA necesita abrir una minuta para la sucursal actual. ¿Deseás abrirla ahora?',
      showCancelButton: true,
      confirmButtonText: 'Sí, abrir minuta',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#14b8a6',
    });
    if (!confirmation.isConfirmed) return;

    try {
      setMinutaState({ loading: true, minuta: null, error: '' });
      const data = await abrirMinuta({ sucursal_id: sucursal.id });
      setMinutaState({ loading: false, minuta: data?.minuta || null, error: '' });
      await Swal.fire({ icon: 'success', title: 'Minuta abierta', text: 'Ya podés registrar la cobranza.' });
    } catch (error) {
      setMinutaState({ loading: false, minuta: null, error: error?.response?.data?.message || 'No se pudo abrir la minuta.' });
    }
  };

  useEffect(() => {
    if (!prefillCliente?.id) return;
    setCliente(prefillCliente);
  }, [prefillCliente]);

  useEffect(() => {
    const cargarDocumentos = async () => {
      if (!cliente?.id || !sucursal?.id) return;
      try {
        const data = await fetchDocumentosCliente({ clienteId: cliente.id, sucursalId: sucursal.id });
        const facturas = [...(data?.facturas || [])];
        if (prefillFacturaId && !facturas.some((factura) => Number(factura.id) === prefillFacturaId)) {
          facturas.unshift({
            id: prefillFacturaId,
            documento_numero: prefillFactura?.numero || `Factura #${prefillFacturaId}`,
            total: Number(prefillFactura?.total || prefillImporte || 0),
            saldo: Number(prefillImporte || prefillFactura?.saldo || 0),
            estado: 'EMITIDA',
          });
        }
        setDocumentos({ facturas, guias: data?.guias || [], ajustes: data?.ajustes || [] });
        const facturasImput = {};
        facturas.forEach((factura) => {
          const esFacturaPrefill = prefillFacturaId && Number(factura.id) === prefillFacturaId;
          facturasImput[factura.id] = esFacturaPrefill
            ? Number(prefillImporte || factura.saldo || 0)
            : 0;
        });
        const guiasImput = {};
        (data?.guias || []).forEach((guia) => {
          if (isContraReceiptMode && prefillGuiaId && Number(guia.id) === Number(prefillGuiaId)) {
            guiasImput[guia.id] = prefillImporte || getPendingContrareembolso(guia);
          } else {
            guiasImput[guia.id] = 0;
          }
        });
        const ajustesImput = {};
        (data?.ajustes || []).forEach((ajuste) => { ajustesImput[ajuste.id] = 0; });
        setImputaciones({ facturas: facturasImput, guias: guiasImput, ajustes: ajustesImput });
        if (prefillFacturaId && Number(prefillImporte || 0) > 0) {
          setPagos([]);
          setPagoDraft({ ...DEFAULT_PAGO, importe: String(prefillImporte) });
          setEditingPagoIndex(-1);
          setTab('facturas');
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'No se pudieron cargar los documentos',
          text: error?.response?.data?.message || 'Intenta nuevamente.',
        });
      }
    };
    cargarDocumentos();
  }, [cliente?.id, isContraReceiptMode, prefillFactura, prefillFacturaId, prefillGuiaId, prefillImporte, sucursal?.id]);

  useEffect(() => {
    if (!cliente?.id || pagos.length || !documentos.facturas.length && !documentos.guias.length && !documentos.ajustes.length) return undefined;
    setGuidedPayment(true);
    const panel = paymentPanelRef.current;
    if (panel) {
      const bounds = panel.getBoundingClientRect();
      if (bounds.top < 0 || bounds.bottom > window.innerHeight) panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    const timeout = window.setTimeout(() => setGuidedPayment(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [cliente?.id, documentos, pagos.length]);

  const resetCobranza = () => {
    setCliente(null);
    setDocumentos({ facturas: [], guias: [], ajustes: [] });
    setImputaciones({ facturas: {}, guias: {}, ajustes: {} });
    setPagos([]);
    setRetenciones([]);
    setRetencionDraft(null);
    setObservaciones('');
    setErrores({});
    setRegistrarSaldoACuenta(false);
    setRendirDirectamente(false);
    setPagoDraft(null);
    setEditingPagoIndex(null);
    setScanModalOpen(false);
    setScanPagoIndex(null);
    setGuidedPayment(false);
    setFechaCobranza(new Date().toISOString().split('T')[0]);
  };

  const handleSelectCliente = async (nextCliente) => {
    const isDifferent = cliente?.id && String(cliente.id) !== String(nextCliente?.id);
    const hasWork = pagos.length || retenciones.length || Object.values(imputaciones.facturas || {}).some(Number) || Object.values(imputaciones.guias || {}).some(Number) || Object.values(imputaciones.ajustes || {}).some(Number) || observaciones.trim();
    if (isDifferent && hasWork) {
      const confirmation = await Swal.fire({
        icon: 'warning', title: 'Cambiar de cliente',
        text: 'Cambiar de cliente descartar� los datos de esta cobranza. �Continuar?',
        showCancelButton: true, confirmButtonText: 'Continuar', cancelButtonText: 'Cancelar',
      });
      if (!confirmation.isConfirmed) return;
      resetCobranza();
    }
    setCliente(nextCliente);
    setShowSelectorCliente(false);
  };

  const updatePagoDraft = (field, value) => {
    setPagoDraft((current) => ({ ...(current || DEFAULT_PAGO), [field]: value }));
  };

  const validatePago = (pago) => {
    const nextErrors = {};
    if (!pago?.medio_pago) nextErrors.pagoDraft = 'Debe seleccionar un medio de pago.';
    if (!pago?.importe || Number(pago.importe) <= 0) nextErrors.pagoImporte = 'Debe indicar un importe valido mayor a cero.';
    if (MEDIOS_CON_REFERENCIA_OBLIGATORIA.has(pago?.medio_pago) && !String(pago?.referencia || '').trim()) nextErrors.pagoReferencia = 'Este medio requiere referencia.';
    if (MEDIOS_CON_BANCO_O_CBU.has(pago?.medio_pago) && !String(pago?.banco || '').trim() && !pago?.banco_id && !String(pago?.cbu || '').trim()) nextErrors.pagoBanco = 'Indica banco o CBU.';
    if (['CHEQUE', 'ECHEQ'].includes(pago?.medio_pago) && !pago?.banco_id) nextErrors.pagoBanco = 'Seleccione un banco activo.';
    if (['CHEQUE', 'ECHEQ'].includes(pago?.medio_pago) && !pago?.fecha_cobro) nextErrors.pagoFechaCobro = 'Indique la fecha de vencimiento/pago del valor.';
    return nextErrors;
  };

  const handleAddPago = () => {
    setErrores((current) => ({ ...current, pagoDraft: undefined, pagoImporte: undefined, pagoReferencia: undefined, pagoBanco: undefined, pagoFechaCobro: undefined }));
    setPagoDraft({ ...DEFAULT_PAGO });
    setEditingPagoIndex(-1);
  };
  const handleEditPago = (index) => {
    setErrores((current) => ({ ...current, pagoDraft: undefined, pagoImporte: undefined, pagoReferencia: undefined, pagoBanco: undefined, pagoFechaCobro: undefined }));
    setPagoDraft({ ...pagos[index] });
    setEditingPagoIndex(index);
  };
  const closePagoEditor = () => {
    setPagoDraft(null);
    setEditingPagoIndex(null);
  };
  const handleSavePago = () => {
    const draftErrors = validatePago(pagoDraft);
    if (Object.keys(draftErrors).length) {
      setErrores((current) => ({ ...current, ...draftErrors }));
      return;
    }
    const savedPago = { ...pagoDraft, importe: String(toAmount(pagoDraft.importe)) };
    setPagos((current) => editingPagoIndex === -1
      ? [...current, savedPago]
      : current.map((pago, index) => (index === editingPagoIndex ? savedPago : pago)));
    closePagoEditor();
  };
  const handleRemovePago = (index) => {
    setPagos((prev) => prev.filter((_, idx) => idx !== index));
    closePagoEditor();
  };
  const handleImputacionChange = (tipo, id, value) => {
    setImputaciones((prev) => normalizeApplications(documentos, {
      ...prev,
      [tipo]: {
        ...prev[tipo],
        [id]: toAmount(value),
      },
    }, totalDisponible, isContraReceiptMode));
  };

  const handleSeleccionDocumento = (tipo, documento, selected) => {
    const saldo = tipo === 'guias' && isContraReceiptMode
      ? getPendingContrareembolso(documento)
      : Number(documento.saldo || 0);
    if (!selected) {
      handleImputacionChange(tipo, documento.id, 0);
      return;
    }
    setImputaciones((prev) => {
      const requested = {
        ...prev,
        [tipo]: { ...prev[tipo], [documento.id]: saldo },
      };
      return normalizeApplications(documentos, requested, totalDisponible, isContraReceiptMode);
    });
  };

  const applyScanToForm = (fields) => {
    if (fields?.fecha) {
      setFechaCobranza(fields.fecha);
    }
    if (fields?.importe || fields?.medioPago || fields?.referencia || fields?.banco) {
      // OCR can suggest a draft, but it never creates an actual payment before Save.
      setPagoDraft((current) => {
        const base = current || { ...DEFAULT_PAGO };
        return {
          ...base,
          medio_pago: fields.medioPago || base.medio_pago,
          referencia: fields.referencia || base.referencia,
          banco: fields.banco || base.banco,
          banco_id: fields.banco_id || base.banco_id,
          importe: fields.importe != null ? String(fields.importe) : base.importe,
        };
      });
      if (editingPagoIndex === null) setEditingPagoIndex(-1);
    }
  };

  const openComprobanteScan = () => {
    setScanPurpose('comprobante');
    setScanPagoIndex(null);
    setScanModalOpen(true);
  };

  const openChequeScan = (index) => {
    setScanPurpose('cheque');
    setScanPagoIndex(index);
    setScanModalOpen(true);
  };

  const applyChequeScansToDraft = (scannedCheques) => {
    if (!scannedCheques?.length) return;
    const fields = scannedCheques[0];
    setPagoDraft((current) => ({
      ...(current || DEFAULT_PAGO),
      medio_pago: current?.medio_pago === 'ECHEQ' ? 'ECHEQ' : 'CHEQUE',
      referencia: fields.numeroCheque || fields.referencia || current?.referencia || '',
      banco: fields.banco || current?.banco || '',
      banco_id: fields.banco_id || current?.banco_id || '',
      importe: fields.importe != null ? String(fields.importe) : current?.importe || '',
      fecha_emision: fields.fechaEmision || fields.fecha || current?.fecha_emision || '',
      fecha_cobro: fields.fechaCobro || current?.fecha_cobro || '',
      cuit_titular: fields.cuit || current?.cuit_titular || '',
    }));
  };

  useEffect(() => {
    if (editingPagoIndex === null) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') closePagoEditor(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editingPagoIndex]);

  const validarFormulario = () => {
    const nuevosErrores = {};

    if (!minutaState.minuta?.id) nuevosErrores.minuta = 'Debe existir una minuta abierta.';
    if (!cliente?.id) nuevosErrores.cliente = 'Debe seleccionar un cliente.';
    if (!fechaCobranza) nuevosErrores.fechaCobranza = 'La fecha de cobranza es obligatoria.';
    if (totalDisponible <= 0) nuevosErrores.totalCobrado = 'Debe ingresar un medio de pago o retención con importe.';
    if (isContraReceiptMode && (totalRetenciones > 0 || totalPagos !== totalSolicitado)) {
      nuevosErrores.totalCobrado = 'En contrareembolso el total cobrado debe coincidir con el total aplicado.';
    }
    if (!isContraReceiptMode && remanente > 0 && !registrarSaldoACuenta) {
      nuevosErrores.aplicaciones = 'El importe sin aplicar debe registrarse expresamente a cuenta.';
    }

    pagos.forEach((pago, idx) => {
      if (!pago.medio_pago) nuevosErrores[`pago-${idx}`] = 'Debe seleccionar un medio de pago.';
      if (!pago.importe || Number(pago.importe) <= 0) {
        nuevosErrores[`pago-importe-${idx}`] = 'Debe indicar un importe valido.';
      }
      if (MEDIOS_CON_REFERENCIA_OBLIGATORIA.has(pago.medio_pago) && !String(pago.referencia || '').trim()) {
        nuevosErrores[`pago-referencia-${idx}`] = 'Este medio requiere referencia.';
      }
      if (MEDIOS_CON_BANCO_O_CBU.has(pago.medio_pago) && !String(pago.banco || '').trim() && !pago.banco_id && !String(pago.cbu || '').trim()) {
        nuevosErrores[`pago-banco-${idx}`] = 'Indica banco o CBU.';
      }
      if (['CHEQUE', 'ECHEQ'].includes(pago.medio_pago) && !pago.banco_id) {
        nuevosErrores[`pago-banco-${idx}`] = 'Seleccione un banco activo.';
      }
      if (['CHEQUE', 'ECHEQ'].includes(pago.medio_pago) && !pago.fecha_cobro) nuevosErrores[`pago-fecha-cobro-${idx}`] = 'Indique la fecha de vencimiento/pago del valor.';
    });

    if (isContraReceiptMode) {
      const totalGuias = Object.values(imputaciones.guias || {}).reduce((acc, val) => acc + Number(val || 0), 0);
      if (totalGuias <= 0) {
        nuevosErrores.aplicaciones = 'Debe aplicar al menos una guia con contrareembolso pendiente.';
      }
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const handleSubmit = async (event, resume = false) => {
    event.preventDefault();
    if (submittingRef.current || (savedOperation && !resume) || (!resume && !validarFormulario())) return;
    submittingRef.current = true;

    const aplicaciones = [];
    const agregarAplicacion = (documentoTipo, documentoId, monto) => {
      const montoAplicado = toAmount(monto);
      if (montoAplicado <= 0) return;
      aplicaciones.push({ documento_tipo: documentoTipo, documento_id: Number(documentoId), monto_aplicado: montoAplicado });
    };

    if (!isContraReceiptMode) {
      Object.entries(imputaciones.facturas || {}).forEach(([facturaId, monto]) => {
        agregarAplicacion('FACTURA', facturaId, monto);
      });
      Object.entries(imputaciones.ajustes || {}).forEach(([ajusteId, monto]) => {
        agregarAplicacion('AJUSTE', ajusteId, monto);
      });
    }

    Object.entries(imputaciones.guias || {}).forEach(([guiaId, monto]) => {
      agregarAplicacion('GUIA_SIN_FACTURA', guiaId, monto);
    });

    if (!isContraReceiptMode && registrarSaldoACuenta && remanente > 0) {
      aplicaciones.push({
        documento_tipo: 'A_CUENTA',
        documento_id: null,
        monto_aplicado: remanente,
      });
    }

    try {
      setIsSubmitting(true);
      const data = await registrarCobranza(resume ? savedOperation.payload : {
        minuta_id: minutaState.minuta?.id,
        sucursal_id: sucursal?.id,
        cliente_id: cliente?.id,
        fecha_hora: `${fechaCobranza} ${new Date().toTimeString().split(' ')[0]}`,
        observaciones,
        contexto: isContraReceiptMode ? 'CONTRAREEMBOLSO' : 'GENERAL',
        rendir_directamente: !isContraReceiptMode && rendirDirectamente,
        retenciones: retenciones.map((retencion) => ({
          cliente_id: Number(cliente.id),
          importe: Number(retencion.importe),
          impuesto: retencion.impuesto,
          fecha: retencion.fecha,
          base_calculo: Number(retencion.importe),
          aplicaciones: aplicaciones.filter((item) => item.documento_tipo !== 'A_CUENTA'),
        })),
        pagos: pagos.map((pago) => ({
          medio_pago: pago.medio_pago,
          referencia: pago.referencia || null,
          banco_id: pago.banco_id ? Number(pago.banco_id) : null,
          banco: pago.banco || null,
          cbu: pago.cbu || null,
          titular: pago.titular || null,
          moneda: pago.moneda || 'ARS',
          cotizacion: pago.cotizacion ? Number(pago.cotizacion) : null,
          fecha_emision: pago.fecha_emision || null,
          fecha_cobro: pago.fecha_cobro || null,
          cuit_titular: pago.cuit_titular || null,
          importe: Number(pago.importe || 0),
        })),
        aplicaciones,
      });

      const rendicionRecibida = Boolean(data?.rendicionRecibida || data?.estadoCustodia === 'RECIBIDO_TESORERIA');
      setComprobante({ ...data, rendicionRecibida });
      resetCobranza();
      window.dispatchEvent(new CustomEvent('cuenta-corriente:refresh', { detail: { clienteId: cliente?.id } }));
      Swal.fire({
        icon: 'success',
        title: isContraReceiptMode ? 'Recibo de contrareembolso generado' : (rendicionRecibida ? 'Cobro registrado y rendido' : 'Cobro registrado'),
        text: isContraReceiptMode
          ? `Recibo ${data?.numeroRecibo || ''} generado correctamente.`
          : rendicionRecibida
            ? `Recibo ${data?.numeroRecibo || ''} generado.`
            : `Recibo ${data?.numeroRecibo || ''} generado. Los valores quedan pendientes de rendici�n; Tesorer�a todav�a no los recibi�.`,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo registrar la cobranza',
        text: error?.response?.data?.message || 'Verifica los datos e intenta nuevamente.',
      });
    } finally {
      submittingRef.current = false;
      refreshOperation();
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))] pb-24">
      <PageHeader
        title={isContraReceiptMode ? 'Ingreso de Recibos' : 'Registrar Cobranza'}
        subtitle={isContraReceiptMode
          ? 'Emision de recibos contra cobranza de clientes con control por guia y medio de pago.'
          : 'Registro de cobranzas y aplicacion a documentos con recibo por sucursal'}
        actions={[
          cliente ? (
            <button
              key="cuenta"
              type="button"
              onClick={() => setAccountOpen(true)}
              className="px-4 py-2 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--brand))] hover:bg-[rgba(var(--muted),0.3)]"
            >
              Ver cuenta corriente del cliente
            </button>
          ) : null,
          <button
            key="volver"
            onClick={() => navigate('/tesoreria/cobranzas')}
            className="px-4 py-2 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)]"
          >
            Volver
          </button>,
        ]}
      />
      <div className="mx-auto w-full max-w-[1400px] px-4"><SucursalContexto /></div>

      {minutaState.error && (
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-500">
            {minutaState.error}
          </div>
        </div>
      )}

      {operationError && <div role="alert" className="mx-auto max-w-[1400px] p-4 text-red-700">{operationError}</div>}
      {savedOperation && <section aria-label="Cobranza guardada" className="mx-auto max-w-[1400px] rounded-lg border border-amber-400 p-4">
        {savedOperation.status === 'pending' ? <>
          <p>Existe una cobranza pendiente de confirmación. Se reenviarán exactamente los datos originales, sin crear otro ingreso.</p>
          <p>Cliente #{savedOperation.payload.cliente_id} · Fecha {savedOperation.payload.fecha_hora}</p>
          <details><summary>Consultar datos originales</summary><pre className="overflow-auto text-xs">{JSON.stringify(savedOperation.payload, null, 2)}</pre></details>
          <button type="button" disabled={isSubmitting} onClick={(event) => handleSubmit(event, true)}>Reintentar cobranza guardada</button>
        </> : <><p>La cobranza anterior fue confirmada. Para registrar otro ingreso, inicie una operación nueva.</p>
          <button type="button" onClick={async () => { try { await startNewReceiptOperation(operationFlow); refreshOperation(); } catch (error) { setSavedOperation(null); setOperationError(error.message); } }}>Nueva cobranza</button></>}
      </section>}
      <form onSubmit={(event) => handleSubmit(event)} className="max-w-[1400px] mx-auto px-4 py-2 grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-8 space-y-4">
          <section aria-labelledby="collection-details-title" className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 id="collection-details-title" className="text-lg font-semibold text-[rgb(var(--fg))]">Datos de la cobranza</h3>
              <button
                type="button"
                onClick={openComprobanteScan}
                title="Cargar una imagen o PDF para sugerir campos del formulario"
                className="inline-flex items-center gap-2 rounded-xl border border-[rgb(var(--border))] px-3 py-1.5 text-sm text-[rgb(var(--fg))]"
              >
                <Camera className="h-4 w-4" aria-hidden="true" />
                Leer comprobante
              </button>
            </div>
            <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[rgb(var(--muted))]">
              {minutaState.loading ? (
                <span>Cargando minuta...</span>
              ) : minutaState.minuta ? (
                <>
                  <span>Minuta activa <strong className="text-[rgb(var(--fg))]">#{minutaState.minuta.numero}</strong></span>
                  <span>Sucursal {labelSucursal(sucursal)}</span>
                  <span className="rounded-full border border-[rgb(var(--border))] px-2 py-0.5 font-semibold">{minutaState.minuta.estado}</span>
                </>
              ) : (
                <span>
                  No hay minuta abierta.{' '}
                  <button type="button" onClick={handleAbrirMinuta} className="text-[rgb(var(--accent))] underline">Abrir minuta</button>
                </span>
              )}
            </div>
            {errores.minuta && <p className="mb-2 text-sm text-red-500">{errores.minuta}</p>}
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-8">
                <label htmlFor="collection-customer" className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">
                  Cliente <span className="text-red-500">*</span>
                </label>
                <button
                  id="collection-customer"
                  type="button"
                  onClick={() => setShowSelectorCliente(true)}
                  className={`w-full px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-left focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
                    errores.cliente ? 'border-red-500' : 'border-[rgb(var(--border))]'
                  }`}
                >
                  {cliente ? `${cliente.nombre || cliente.razonSocial || cliente.razon_social || 'Cliente sin nombre'} (${cliente.cuit || 'Sin CUIT'})` : 'Seleccionar cliente'}
                </button>
                {errores.cliente && <p className="mt-1 text-xs text-red-500">{errores.cliente}</p>}
                {isContraReceiptMode && (
                  <p className="mt-2 text-xs text-[rgb(var(--muted))]">
                    Modo contrareembolso: no mezcla facturas, no manda remanentes a cuenta y exige guia entregada con CR pendiente.
                  </p>
                )}
                {!isContraReceiptMode && can('COBRANZAS_RECIBIR_TESORERIA') && (
                  <label className="mt-3 flex items-start gap-2 text-sm text-[rgb(var(--fg))]">
                    <input type="checkbox" checked={rendirDirectamente} onChange={(event) => setRendirDirectamente(event.target.checked)} aria-describedby="direct-rendition-help" />
                    <span>
                      <span className="block font-medium">Registrar como rendido</span>
                      <span id="direct-rendition-help" className="block text-xs text-[rgb(var(--muted))]">Marc&aacute; esta opci&oacute;n si el dinero o valor ya fue recibido por Tesorer&iacute;a. El cobro no quedar&aacute; pendiente de rendici&oacute;n.</span>
                    </span>
                  </label>
                )}
              </div>
              <div className="col-span-12 md:col-span-4">
                <label htmlFor="collection-date" className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">Fecha de cobranza</label>
                <input
                  id="collection-date"
                  type="date"
                  value={fechaCobranza}
                  onChange={(e) => setFechaCobranza(e.target.value)}
                  className={`w-full px-4 py-2.5 rounded-xl border bg-[rgba(var(--bg))] text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))] ${
                    errores.fechaCobranza ? 'border-red-500' : 'border-[rgb(var(--border))]'
                  }`}
                />
                {errores.fechaCobranza && <p className="mt-1 text-xs text-red-500">{errores.fechaCobranza}</p>}
              </div>
            </div>
          </section>

          {!isContraReceiptMode && <section aria-label="Retenciones recibidas" className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4">
            <div className="mb-3 flex items-center justify-between"><h3 className="text-lg font-semibold">Retenciones recibidas</h3><button type="button" onClick={() => setRetencionDraft({ impuesto: '', importe: '', fecha: fechaCobranza })} className="rounded-xl border px-3 py-1.5 text-sm">Agregar retención</button></div>
            {retencionDraft && <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <label className="text-sm">Tipo de retención *<select aria-label="Tipo de retención" value={retencionDraft.impuesto} onChange={(event) => setRetencionDraft({ ...retencionDraft, impuesto: event.target.value })} className="mt-1 w-full rounded border p-2"><option value="">{tiposRetencion.length ? 'Seleccionar tipo' : 'No hay tipos de retención configurados'}</option>{tiposRetencion.map((tipo) => <option key={tipo.id} value={tipo.nombre}>{tipo.nombre}</option>)}</select></label>
              <label className="text-sm">Importe *<input aria-label="Importe retención" type="number" min="0.01" step="0.01" value={retencionDraft.importe} onChange={(event) => setRetencionDraft({ ...retencionDraft, importe: event.target.value })} className="mt-1 w-full rounded border p-2" /></label>
              <label className="text-sm">Fecha *<input aria-label="Fecha retención" type="date" value={retencionDraft.fecha} onChange={(event) => setRetencionDraft({ ...retencionDraft, fecha: event.target.value })} className="mt-1 w-full rounded border p-2" /></label>
              <div className="flex items-end gap-2"><button type="button" onClick={() => { if (retencionDraft.impuesto.trim() && Number(retencionDraft.importe) > 0 && retencionDraft.fecha) { setRetenciones((items) => [...items, { ...retencionDraft, importe: String(toAmount(retencionDraft.importe)) }]); setRetencionDraft(null); } }} className="rounded-xl bg-[rgb(var(--brand))] px-3 py-2 text-white">Guardar retención</button><button type="button" onClick={() => setRetencionDraft(null)} className="rounded-xl border px-3 py-2">Cancelar</button></div>
            </div>}
            {retenciones.map((retencion, index) => <div key={`${retencion.impuesto}-${index}`} className="mt-2 flex justify-between rounded border p-3 text-sm"><span>{retencion.impuesto} · {retencion.fecha}</span><span>{formatCurrency(retencion.importe)} <button type="button" aria-label={`Quitar retención ${index + 1}`} onClick={() => setRetenciones((items) => items.filter((_, itemIndex) => itemIndex !== index))}>×</button></span></div>)}
          </section>}

          </div>

          <section aria-label="Documentos a aplicar" className="col-span-12 lg:col-span-8 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-4">
            <div className="flex flex-wrap items-center gap-3 border-b border-[rgb(var(--border))] pb-4">
              {(isContraReceiptMode ? ['guias'] : ['facturas', 'guias']).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setTab(item)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    tab === item
                      ? 'bg-[rgb(var(--brand))] text-white'
                      : 'border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)]'
                  }`}
                >
                  {item === 'facturas' && 'Facturas con saldo'}
                  {item === 'guias' && (isContraReceiptMode ? 'Guias con contrareembolso' : 'Guias sin factura')}
                </button>
              ))}
            </div>

            {!isContraReceiptMode && tab === 'facturas' && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[rgba(var(--subpanel))]">
                    <tr>
                      <th className="w-10 px-3 py-2" aria-label="Seleccionar factura" />
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                        Factura
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                        Fecha
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                        Subcuenta
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                        Saldo
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                        Aplicar
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--border))]">
                    {documentos.facturas.length ? (
                      documentos.facturas.map((factura) => (
                        <tr key={factura.id}>
                          <td className="px-3 py-2 text-center"><input type="checkbox" checked={Number(imputaciones.facturas[factura.id] || 0) > 0} onChange={(event) => handleSeleccionDocumento('facturas', factura, event.target.checked)} aria-label={`Aplicar factura ${factura.documento_numero}`} /></td>
                          <td className="px-3 py-2 text-sm text-[rgb(var(--fg))]">{factura.documento_numero}</td>
                          <td className="px-3 py-2 text-sm text-[rgb(var(--fg))]">{factura.doc_fecha}</td>
                          <td className="px-3 py-2 text-sm text-[rgb(var(--fg))]">{[factura.subcuenta_codigo, factura.subcuenta_nombre].filter(Boolean).join(' / ') || '-'}</td>
                          <td className="px-3 py-2 text-sm text-right text-[rgb(var(--fg))]">
                            {formatCurrency(factura.saldo)}
                          </td>
                          <td className="px-3 py-2 text-sm text-right">
                            <input
                              type="number"
                              min="0"
                              max={factura.saldo}
                              step="0.01"
                              value={imputaciones.facturas[factura.id] || ''}
                              onChange={(e) => handleImputacionChange('facturas', factura.id, e.target.value)}
                              className="w-32 px-2 py-1.5 rounded-lg border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-right"
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-3 py-3 text-sm text-[rgb(var(--muted))] text-center">
                          No hay facturas con saldo.
                        </td>
                      </tr>
                    )}
                    {(documentos.ajustes || []).map((ajuste) => (
                      <tr key={`ajuste-${ajuste.id}`}>
                        <td className="px-3 py-2 text-center"><input type="checkbox" checked={Number(imputaciones.ajustes?.[ajuste.id] || 0) > 0} onChange={(event) => handleSeleccionDocumento('ajustes', ajuste, event.target.checked)} aria-label={`Aplicar ajuste ${ajuste.documento_numero}`} /></td>
                        <td className="px-3 py-2 text-sm text-[rgb(var(--fg))]">{ajuste.documento_numero}<span className="ml-2 text-xs text-[rgb(var(--muted))]">Ajuste débito · {ajuste.motivo}</span></td>
                        <td className="px-3 py-2 text-sm text-[rgb(var(--fg))]">{ajuste.doc_fecha}</td>
                        <td className="px-3 py-2 text-sm text-right text-[rgb(var(--fg))]">{formatCurrency(ajuste.saldo)}</td>
                        <td className="px-3 py-2 text-sm text-right"><input type="number" min="0" max={ajuste.saldo} step="0.01" value={imputaciones.ajustes?.[ajuste.id] || ''} onChange={(e) => handleImputacionChange('ajustes', ajuste.id, e.target.value)} className="w-32 px-2 py-1.5 rounded-lg border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-right" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'guias' && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[rgba(var(--subpanel))]">
                    <tr>
                      <th className="w-10 px-3 py-2" aria-label="Seleccionar guía" />
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                        Guia
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                        Fecha
                      </th>
                      {isContraReceiptMode && (
                        <th className="px-3 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                          CR pendiente
                        </th>
                      )}
                      <th className="px-3 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                        Saldo
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-[rgb(var(--muted))] uppercase">
                        Aplicar
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--border))]">
                    {documentos.guias.length ? (
                      documentos.guias.map((guia) => (
                        <tr key={guia.id}>
                          <td className="px-3 py-2 text-center"><input type="checkbox" checked={Number(imputaciones.guias[guia.id] || 0) > 0} onChange={(event) => handleSeleccionDocumento('guias', guia, event.target.checked)} aria-label={`Aplicar guía ${guia.documento_numero}`} /></td>
                          <td className="px-3 py-2 text-sm text-[rgb(var(--fg))]">{guia.documento_numero}</td>
                          <td className="px-3 py-2 text-sm text-[rgb(var(--fg))]">{guia.doc_fecha}</td>
                          {isContraReceiptMode && (
                            <td className="px-3 py-2 text-sm text-right text-[rgb(var(--fg))]">
                              {formatCurrency(getPendingContrareembolso(guia))}
                            </td>
                          )}
                          <td className="px-3 py-2 text-sm text-right text-[rgb(var(--fg))]">
                            {formatCurrency(guia.saldo)}
                          </td>
                          <td className="px-3 py-2 text-sm text-right">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={imputaciones.guias[guia.id] || ''}
                              onChange={(e) => handleImputacionChange('guias', guia.id, e.target.value)}
                              className="w-32 px-2 py-1.5 rounded-lg border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-right"
                              max={isContraReceiptMode ? getPendingContrareembolso(guia) : undefined}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={isContraReceiptMode ? 6 : 5} className="px-3 py-3 text-sm text-[rgb(var(--muted))] text-center">
                          {isContraReceiptMode ? 'No hay guias con contrareembolso pendiente.' : 'No hay guias pendientes.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {!isContraReceiptMode && remanente > 0 && (
              <label className="mt-4 flex items-center gap-2 text-sm text-[rgb(var(--fg))]">
                <input type="checkbox" checked={registrarSaldoACuenta} onChange={(event) => setRegistrarSaldoACuenta(event.target.checked)} />
                Registrar {formatCurrency(remanente)} a cuenta
              </label>
            )}

            {errores.aplicaciones && <p className="mt-3 text-xs text-red-500">{errores.aplicaciones}</p>}
          </section>

        <aside aria-label="Resumen de cobranza" className="col-span-12 space-y-4 lg:col-span-4 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100dvh-2rem)] lg:overflow-y-auto">
          <section ref={paymentPanelRef} aria-label="Medios de pago" className={`rounded-2xl border bg-[rgba(var(--panel))] p-4 shadow-[var(--shadow)] transition-all ${guidedPayment ? 'border-[rgb(var(--brand))] shadow-[0_0_0_4px_rgba(var(--brand),0.14)]' : 'border-[rgb(var(--border))]'}`}>
            <div className="mb-3 flex items-center justify-between"><h3 className="text-lg font-semibold">Medios de pago</h3><button type="button" onClick={handleAddPago} className="inline-flex items-center gap-1 rounded-xl border border-[rgb(var(--border))] px-3 py-1.5 text-sm"><Plus className="h-4 w-4" />Agregar medio</button></div>
            <>{guidedPayment && !pagos.length && <p className="mb-3 rounded-xl bg-[rgba(var(--brand),0.1)] px-3 py-2 text-sm text-[rgb(var(--brand))]">Ahora agreg� el medio de pago</p>}<div className="space-y-2">{pagos.length ? pagos.map((pago, index) => <button key={`summary-${index}`} type="button" onClick={() => handleEditPago(index)} className="flex w-full items-center justify-between rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] p-3 text-left"><span><strong className="block">{MEDIOS_PAGO.find((medio) => medio.value === pago.medio_pago)?.label}</strong><small className="text-[rgb(var(--muted))]">{pago.referencia || 'Completar datos'}</small></span><strong>{formatCurrency(pago.importe)}</strong></button>) : <div className="rounded-xl border border-dashed border-[rgb(var(--border))] px-3 py-5 text-center text-sm text-[rgb(var(--muted))]">Todavia no agregaste medios de pago</div>}</div></>
          </section>
          <div role="dialog" aria-modal="true" aria-label="Editar medio de pago" className={editingPagoIndex === null ? 'hidden' : 'fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4'}>
            <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[rgb(var(--fg))]">Medios de pago</h3>
              <button
                type="button"
                onClick={closePagoEditor}
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))] hover:bg-[rgba(var(--muted),0.3)]"
              >
                Cancelar
              </button>
            </div>
            <div className="space-y-4">
              {pagoDraft && (
                <div
                  key={`pago-${editingPagoIndex}`}
                  className="grid grid-cols-12 gap-4 border border-[rgb(var(--border))] rounded-2xl p-4"
                >
                  <div className="col-span-12 md:col-span-4">
                    <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">Medio</label>
                    <select
                      value={pagoDraft.medio_pago}
                      onChange={(e) => updatePagoDraft( 'medio_pago', e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))]"
                    >
                      {MEDIOS_PAGO.map((medio) => (
                        <option key={medio.value} value={medio.value}>
                          {medio.label}
                        </option>
                      ))}
                    </select>
                    {errores.pagoDraft && <p className="mt-1 text-xs text-red-500">{errores.pagoDraft}</p>}
                  </div>
                  {['CHEQUE', 'ECHEQ'].includes(pagoDraft.medio_pago) && <div className="col-span-12 md:col-span-4 flex items-end"><button type="button" onClick={() => openChequeScan(editingPagoIndex)} className="flex w-full items-center justify-center gap-2 rounded-xl border border-[rgb(var(--brand))] px-3 py-2 text-sm font-semibold text-[rgb(var(--brand))]"><Camera className="w-4 h-4" /> Cargar cheque con foto</button></div>}
                  <div className="col-span-12 md:col-span-4">
                    <label className="block text-sm font-medium text-[rgb(var(--fg))] mb-1.5">Importe</label>
                    <input
                      type="number"
                      step="0.01"
                      value={pagoDraft.importe}
                      onChange={(e) => updatePagoDraft( 'importe', e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] text-[rgb(var(--fg))]"
                    />
                    {errores.pagoImporte && (
                      <p className="mt-1 text-xs text-red-500">{errores.pagoImporte}</p>
                    )}
                  </div>
                  {pagoDraft.medio_pago !== 'EFECTIVO' && <div className="col-span-12 md:col-span-4"><label className="block text-sm font-medium mb-1.5">{getReferenciaLabel(pagoDraft.medio_pago)}</label><input type="text" value={pagoDraft.referencia} onChange={(e) => updatePagoDraft('referencia', e.target.value)} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-3 py-2" />{errores.pagoReferencia && <p className="mt-1 text-xs text-red-500">{errores.pagoReferencia}</p>}</div>}
                   {['CHEQUE', 'ECHEQ'].includes(pagoDraft.medio_pago) && <>
                     <div className="col-span-12 md:col-span-4"><label className="block text-sm font-medium mb-1.5">Fecha de vencimiento *</label><input type="date" value={pagoDraft.fecha_cobro} onChange={(e) => updatePagoDraft('fecha_cobro', e.target.value)} className="w-full rounded-xl border p-2" />{errores.pagoFechaCobro && <p className="text-xs text-red-500">{errores.pagoFechaCobro}</p>}</div>
                     <div className="col-span-12 md:col-span-4"><label className="block text-sm font-medium mb-1.5">Banco *</label><Combobox options={bancosActivos.map((bank) => ({ value: String(bank.id), label: `${bank.codigo ? `${bank.codigo} · ` : ''}${bank.nombre}` }))} value={String(pagoDraft.banco_id || '')} onChange={(value) => { const bank = bancosActivos.find((item) => Number(item.id) === Number(value)); setPagoDraft((current) => ({ ...current, banco_id: value, banco: bank?.nombre || '' })); }} placeholder="Seleccionar banco activo" searchPlaceholder="Buscar banco..." className="w-full" />{errores.pagoBanco && <p className="text-xs text-red-500">{errores.pagoBanco}</p>}</div>
                     {pagoDraft.medio_pago === 'ECHEQ' && <div className="col-span-12 md:col-span-4"><label className="block text-sm font-medium mb-1.5">CUIT titular / emisor</label><input type="text" value={pagoDraft.cuit_titular} onChange={(e) => updatePagoDraft('cuit_titular', e.target.value)} className="w-full rounded-xl border p-2" /></div>}
                   </>}
                   {pagoDraft.medio_pago === 'TRANSFERENCIA' && <div className="col-span-12 md:col-span-4"><label className="block text-sm font-medium mb-1.5">Fecha de operaci�n</label><input type="date" value={pagoDraft.fecha_cobro} onChange={(e) => updatePagoDraft('fecha_cobro', e.target.value)} className="w-full rounded-xl border p-2" /></div>}
                   {['TRANSFERENCIA', 'DEPOSITO'].includes(pagoDraft.medio_pago) && <div className="col-span-12 md:col-span-4"><label className="block text-sm font-medium mb-1.5">Banco o CBU</label><input type="text" value={pagoDraft.banco || pagoDraft.cbu} onChange={(e) => updatePagoDraft('banco', e.target.value)} className="w-full rounded-xl border p-2" /></div>}
                   {['CHEQUE', 'ECHEQ'].includes(pagoDraft.medio_pago) && <details className="col-span-12 rounded-xl border p-3"><summary className="cursor-pointer font-medium">M&aacute;s datos del valor</summary><div className="mt-3 grid gap-3 md:grid-cols-3"><label className="text-sm">Fecha de emisi&oacute;n<input type="date" value={pagoDraft.fecha_emision} onChange={(e) => updatePagoDraft('fecha_emision', e.target.value)} className="mt-1 w-full rounded border p-2" /></label><label className="text-sm">Titular<input type="text" value={pagoDraft.titular} onChange={(e) => updatePagoDraft('titular', e.target.value)} className="mt-1 w-full rounded border p-2" /></label><label className="text-sm">CBU<input type="text" value={pagoDraft.cbu} onChange={(e) => updatePagoDraft('cbu', e.target.value)} className="mt-1 w-full rounded border p-2" /></label>{pagoDraft.medio_pago === 'CHEQUE' && <label className="text-sm">CUIT titular<input type="text" value={pagoDraft.cuit_titular} onChange={(e) => updatePagoDraft('cuit_titular', e.target.value)} className="mt-1 w-full rounded border p-2" /></label>}</div></details>}
                  {editingPagoIndex >= 0 && (
                    <div className="col-span-12 flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleRemovePago(editingPagoIndex)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-red-500/40 text-red-500 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                        Quitar medio
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={closePagoEditor} className="rounded-xl border border-[rgb(var(--border))] px-4 py-2 text-sm">Cancelar</button>
              <button type="button" onClick={handleSavePago} className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white">{editingPagoIndex === -1 ? 'Guardar medio' : 'Guardar cambios'}</button>
            </div>
            </div>
          </div>
          <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-4">
            <h3 className="text-lg font-semibold text-[rgb(var(--fg))] mb-4">Resumen</h3>
            {isContraReceiptMode && (
              <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700">
                Modo contra reembolso: el recibo se emite solo contra guias entregadas con CR pendiente y con medios trazables.
              </div>
            )}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Total cobrado</span>
                <span className="font-semibold">{formatCurrency(totalPagos)}</span>
              </div>
              <div className="flex justify-between">
                <span>Total retenciones</span>
                <span className="font-semibold">{formatCurrency(totalRetenciones)}</span>
              </div>
              <div className="flex justify-between">
                <span>Total aplicado</span>
                <span className="font-semibold">{formatCurrency(totalAplicado)}</span>
              </div>
              <div className="flex justify-between">
                <span>Sin aplicar</span>
                <span className="font-semibold">{formatCurrency(remanente)}</span>
              </div>
              <div className="flex justify-between">
                <span>Saldo restante</span>
                <span className="font-semibold">{formatCurrency(saldoRestanteDocumentos)}</span>
              </div>
            </div>
            {remanente > 0 && !isContraReceiptMode && (
              <div className="mt-4 rounded-xl border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-sm text-blue-800">
                {registrarSaldoACuenta
                  ? `Se registrarán ${formatCurrency(remanente)} a cuenta.`
                  : `Hay ${formatCurrency(remanente)} sin aplicar.`}
              </div>
            )}
            {saldoRestanteDocumentos > 0 && totalAplicado > 0 && (
              <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-800">
                Cobranza parcial. Luego de esta operación quedarán {formatCurrency(saldoRestanteDocumentos)} pendientes.
              </div>
            )}
            <label className="mt-4 block text-sm font-medium text-[rgb(var(--fg))]">Observaciones
              <textarea value={observaciones} onChange={(event) => setObservaciones(event.target.value)} rows={3} maxLength={1000} className="mt-1.5 w-full resize-y rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] px-3 py-2 text-sm" placeholder="Referencia o detalle para el recibo" />
            </label>
            {errores.totalCobrado && <p className="mt-2 text-xs text-red-500">{errores.totalCobrado}</p>}
            <button
              type="submit"
              disabled={isSubmitting || Boolean(savedOperation) || Boolean(operationError) || !minutaState.minuta?.id || !cliente?.id || totalDisponible <= 0 || (isContraReceiptMode ? (totalRetenciones > 0 || totalPagos !== totalAplicado) : (remanente > 0 && !registrarSaldoACuenta))}
              className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSubmitting ? 'Confirmando...' : (isContraReceiptMode ? 'Emitir recibo' : 'Confirmar cobranza')}
            </button>
          </div>

          {comprobante && (
            <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-4">
              <div className="flex items-center gap-2 text-[rgb(var(--success))]">
                <CheckCircle className="w-5 h-5" />
                <h3 className="text-lg font-semibold">Cobro registrado &middot; {comprobante?.rendicionRecibida ? 'rendido' : 'pendiente de rendici\u00f3n'}</h3>
              </div>
              <p className="mt-2 text-sm text-[rgb(var(--fg))]">
                Numero: <span className="font-semibold">{comprobante.numeroRecibo}</span>
              </p>
              <p className="mt-1 text-sm text-[rgb(var(--fg))]">
                Total del recibo (pagos): <span className="font-semibold">{formatCurrency(comprobante.totalCobrado)}</span>
              </p>
              <p className="mt-1 text-sm text-[rgb(var(--fg))]">
                Total cancelado (pagos + retenciones): <span className="font-semibold">{formatCurrency(comprobante.totalCancelado)}</span>
              </p>
              <p className="mt-1 text-sm text-[rgb(var(--muted))]">
                {comprobante?.rendicionRecibida
                  ? 'El cobro fue recibido por Tesorer\u00eda y sus valores ya no quedan pendientes.'
                  : 'Los valores quedan pendientes de rendici\u00f3n; Tesorer\u00eda todav\u00eda no los recibi\u00f3.'}
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => navigate(`/tesoreria/recibos/${comprobante.reciboId}`)}
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-[rgb(var(--brand))] text-white hover:bg-[rgb(var(--brand-700))]"
                >
                  <Printer className="w-4 h-4" />
                  Ver e imprimir recibo
                </button>
                {location.state?.returnTo ? (
                  <button
                    type="button"
                    onClick={() => navigate(location.state.returnTo, { replace: true })}
                    className="px-4 py-2 rounded-xl border border-[rgb(var(--brand))] text-[rgb(var(--brand))]"
                  >
                    Volver a la cuenta corriente
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => navigate('/tesoreria/cobranzas')}
                  className="px-4 py-2 rounded-xl border border-[rgb(var(--border))] text-[rgb(var(--fg))]"
                >
                  Volver al listado
                </button>
              </div>
            </div>
          )}
        </aside>
      </form>

      <SelectorClienteProveedor
        isOpen={showSelectorCliente}
        onClose={() => setShowSelectorCliente(false)}
        onSelect={handleSelectCliente}
        mode="cliente"
        selectedId={cliente?.id}
      />
      <ComprobanteScanModal
        isOpen={scanModalOpen}
        onClose={() => setScanModalOpen(false)}
        sucursalId={sucursal?.id}
        purpose={scanPurpose}
        onApply={(fields) => {
          if (scanPurpose === 'cheque') {
            applyChequeScansToDraft(Array.isArray(fields) ? fields : [fields]);
          } else {
            applyScanToForm(fields);
          }
          setScanModalOpen(false);
        }}
      />
      <AccountStatementModal
        cliente={cliente}
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
      />
    </div>
  );
}
